import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { spacing } from "@styling/sizing";
import type { RestCourse, RestCreateCourse } from "@KassaroAB/shared/types/course";
import { FormSection } from "../../components/FormSection";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetCoursesQuery } from "../../query/coursesQuery";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

interface CourseFormProps {
  formId: string;
  course?: RestCourse;
  savedTick?: number;
  onSubmit: (data: RestCreateCourse) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const CourseForm = ({
  formId,
  course,
  savedTick,
  onSubmit,
  onDirtyChange,
}: CourseFormProps) => {
  const { t } = useTranslation();
  const { scope } = useCatalogScope();
  const { data: courses } = useGetCoursesQuery(scope);
  const maxSortOrder =
    courses?.reduce((max, item) => Math.max(max, item.sortOrder), 0) ?? 0;
  const initialSortOrder: number | "" =
    course?.sortOrder ?? maxSortOrder + 1;

  const form = useForm({
    defaultValues: {
      name: course?.name ?? "",
      sortOrder: initialSortOrder as number | "",
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        sortOrder: value.sortOrder === "" ? 0 : Number(value.sortOrder),
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("courseForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("courseForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="sortOrder">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("courseForm.sortOrder")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(Number(e.target.value) || 0)
                  }
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
